/**
 * @file Requetes LECTURE SEULE pour l'UI (view-models : cartes d'upgrade, armes, rebirth, etoiles, missions, modes...). Aucune mutation.
 */
import type { Decimal } from '@shared/bignum';
import { UPGRADE_IDS } from '@shared/enums';
import type { GradeId, ModeId, StatId, UpgradeId } from '@shared/enums';
import type { Uid } from '@shared/types';
import type { BuffId, ModeDef, WeaponDef } from '@config/types';
import type { FeatureId } from '@config/unlocks';
import type { SimContext } from './Context';
import { stageValueToDisplay } from './balance/formulas';
import type { StageDisplay } from './balance/formulas';
import type { OfflineReport, RebirthStat, WeaponItem } from './state/PlayerData';
import type { StatBreakdown } from './stats/StatAggregator';
import type { ResolvedWeapon } from './weapons/WeaponStats';
import { resolveWeapon } from './weapons/WeaponStats';

export interface UpgradeCardView {
  id: UpgradeId;
  level: number;
  maxed: boolean;
  cost: Decimal;
  affordable: boolean;
  maxAffordable: number;
  multiplier: Decimal;
  nextMultiplier: Decimal;
}

export interface WeaponItemView {
  item: WeaponItem;
  def: WeaponDef;
  equippedSlot: number | null;
  /** Une autre arme fusionnable avec celle-ci existe (pastille "merge" dans l'inventaire). */
  mergeable: boolean;
}

export interface ModeCardView {
  def: ModeDef;
  unlocked: boolean;
  tickets: number | null;
  nextTicketIn: number | null;
  best: number | null;
}

export class Queries {
  constructor(private readonly ctx: SimContext) {}

  stage(): StageDisplay {
    return stageValueToDisplay(this.ctx.config.balance, this.ctx.data.run.stageValue);
  }

  upgradeCard(id: UpgradeId): UpgradeCardView {
    const u = this.ctx.services.upgrades;
    const level = u.level(id);
    const cost = u.cost(id);
    return {
      id,
      level,
      maxed: u.isMax(id),
      cost,
      affordable: this.ctx.wallet.get('money').gte(cost),
      maxAffordable: u.maxAffordable(id),
      multiplier: u.multiplier(id, level),
      nextMultiplier: u.multiplier(id, level + 1),
    };
  }

  upgradeCards(): UpgradeCardView[] {
    return UPGRADE_IDS.map((id) => this.upgradeCard(id));
  }

  weapons(): WeaponItemView[] {
    const d = this.ctx.data.weapons;
    const svc = this.ctx.services.weapons;
    return d.items
      .map((item) => {
        const slot = d.equipped.indexOf(item.uid);
        return {
          item,
          def: this.ctx.index.weapon(item.defId),
          equippedSlot: slot >= 0 ? slot : null,
          mergeable: d.items.some((o) => o.uid !== item.uid && svc.canMerge(item.uid, o.uid)),
        };
      })
      .sort((a, b) => b.def.baseDamage / b.def.interval - a.def.baseDamage / a.def.interval || a.item.obtainedSeq - b.item.obtainedSeq);
  }

  equippedWeapon(slot: number): ResolvedWeapon | null {
    return this.ctx.weaponStats.forSlot(this.ctx.data, slot, this.ctx.modes.currentId);
  }

  teamDps(): Decimal {
    return this.ctx.weaponStats.teamDps(this.ctx.data, this.ctx.modes.currentId);
  }

  canMerge(a: Uid, b: Uid): boolean {
    return this.ctx.services.weapons.canMerge(a, b);
  }

  /**
   * Caracteristiques d'une arme du joueur telle qu'elle tirerait (panneau de details) : dans son emplacement si elle est equipee,
   * sinon comme si elle etait equipee dans `slot` (etoiles de l'emplacement comprises). Non mis en cache (appel ponctuel).
   */
  weaponPreview(uid: Uid, slot = 0): ResolvedWeapon | null {
    const d = this.ctx.data.weapons;
    const item = d.items.find((w) => w.uid === uid);
    if (!item) return null;
    const eq = d.equipped.indexOf(uid);
    return resolveWeapon(this.ctx.config, this.ctx.index.weapon(item.defId), uid, eq >= 0 ? eq : slot, this.ctx.stats, this.ctx.modes.currentId);
  }

  /**
   * Meilleur partenaire de fusion pour `uid` (bouton « Fusionner » du panneau de details) : de preference une arme EQUIPEE (le resultat
   * reste equipe dans son emplacement), sinon la premiere arme du coffre compatible. Retourne la paire ordonnee [equipee d'abord, autre].
   */
  mergePartner(uid: Uid): [Uid, Uid] | null {
    const d = this.ctx.data.weapons;
    const svc = this.ctx.services.weapons;
    const partners = d.items.filter((o) => svc.canMerge(uid, o.uid));
    if (partners.length === 0) return null;
    const equipped = partners.find((o) => d.equipped.includes(o.uid));
    const other = (equipped ?? partners[0]) as (typeof partners)[number];
    return d.equipped.includes(uid) || !equipped ? [uid, other.uid] : [other.uid, uid];
  }

  collectionProgress(grade: GradeId): { seen: number; total: number; claimed: boolean } {
    const defs = this.ctx.index.weaponsOfGrade(grade);
    const col = this.ctx.data.weapons.collection;
    return {
      seen: defs.filter((d) => col[d.id]?.seen).length,
      total: defs.length,
      claimed: this.ctx.data.weapons.collectionClaimed.includes(`grade:${grade}`),
    };
  }

  rebirth(): { unlocked: boolean; can: boolean; rubies: Decimal; rubiesX3: Decimal; dailyAvailable: boolean; costs: Record<RebirthStat, Decimal> } {
    const r = this.ctx.services.rebirth;
    return {
      unlocked: r.isUnlocked(),
      can: r.canRebirth(),
      rubies: r.previewRubies(false),
      rubiesX3: r.previewRubies(true),
      dailyAvailable: r.dailyBonusAvailable(),
      costs: { damage: r.upgradeCost('damage'), attackSpeed: r.upgradeCost('attackSpeed'), money: r.upgradeCost('money') },
    };
  }

  star(slot: number): { level: number; chance: number; multiplier: Decimal } {
    const s = this.ctx.services.stars;
    return { level: s.level(slot), chance: s.chance(slot), multiplier: s.multiplier(slot) };
  }

  buff(id: BuffId): { seconds: number; superActive: boolean; freeIn: number } {
    const b = this.ctx.data.buffs[id];
    return { seconds: b.seconds, superActive: b.superActive, freeIn: this.ctx.services.buffs.freeReadyIn(id) };
  }

  modes(): ModeCardView[] {
    const unlocks = this.ctx.services.unlocks;
    const tickets = this.ctx.services.tickets;
    return (Object.values(this.ctx.config.modes) as ModeDef[]).map((def) => ({
      def,
      unlocked: unlocks.isUnlocked(def.entry.unlock),
      tickets: def.entry.ticket ? tickets.count(def.entry.ticket) : null,
      nextTicketIn: def.entry.ticket ? tickets.nextIn(def.entry.ticket) : null,
      best: this.ctx.data.progress.modeBests[def.id as ModeId] ?? null,
    }));
  }

  feature(f: FeatureId): boolean {
    return this.ctx.services.unlocks.isFeatureUnlocked(f);
  }

  offline(): OfflineReport | null {
    return this.ctx.services.offline.pending;
  }

  passTier(): number {
    return this.ctx.services.pass.currentTier();
  }

  dailyAvailable(): boolean {
    return this.ctx.services.daily.canClaim();
  }

  statBreakdown(stat: StatId): StatBreakdown {
    return this.ctx.stats.breakdown(stat, { mode: this.ctx.modes.currentId });
  }
}
